/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LinkedInStyleSelect from '../components/LinkedInStyleSelect';
import { buildApiUrl } from '../config/api';
import {
  countries,
  currencies,
  industries,
  allSkills,
  educationLevels,
  internshipDurations,
  stipendTypes,
  workingDaysPerWeek,
  visibilityOptions
} from '../data/formOptions';
import '../styles/PostInternship.css';

const PostInternship = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  // Get user role and preferred back route
  const userRole = localStorage.getItem('role') || localStorage.getItem('userType') || 'recruiter';
  const roleLower = userRole.toLowerCase();
  const fromRoute = (location.state && location.state.from) || null;
  const defaultDashboardRoute = roleLower.includes('association')
    ? '/association-dashboard?section=internships'
    : roleLower.includes('employer')
      ? '/employer-dashboard'
      : '/recruiter-dashboard';
  const backRoute = fromRoute || defaultDashboardRoute;
  const isRecruiter = roleLower === 'recruiter';

  const [userProfile, setUserProfile] = React.useState(null);
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(buildApiUrl('/api/user/profile'), {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : {}).then(setUserProfile).catch(() => setUserProfile({}));
  }, []);

  // Pre-fill company from profile for employers/associations (recruiters must enter it)
  React.useEffect(() => {
    if (!isRecruiter && userProfile) {
      const orgName = userProfile.companyName || userProfile.associationName || userProfile.universityName || userProfile.ngoName || '';
      if (orgName) {
        setFormData(prev => ({ ...prev, company: prev.company || orgName }));
      }
    }
  }, [isRecruiter, userProfile]);
  const [formData, setFormData] = useState({
    // Basic Details
    title: '',
    internshipType: 'Part-time',
    duration: '3 Months',
    stipendType: 'Paid',
    stipendAmount: '',
    stipendCurrency: 'USD',
    eligibility: 'Undergraduate',
    openings: 1,
    skills: [],
    industry: [],
    
    // Description
    about: '',
    responsibilities: '',
    learningOpportunities: '',
    mentorship: true,
    certificate: true,
    jobOfferPossible: false,
    
    // Location & Duration
    startDate: '',
    endDate: '',
    location: '',
    country: 'Kenya',
    city: '',
    workMode: 'On-site',
    workingDays: '5 days',
    workingHours: '9 AM - 5 PM',
    
    // Contact & Submission
    applicationDeadline: '',
    contactEmail: '',
    hrName: '',
    company: '',
    companyDescription: '',
    companyWebsite: '',
    companyLinkedIn: '',
    visibility: 'public',
    // Privacy controls
    showCompanyName: true,
    showHrName: true
  });

  const internshipTypes = ['Full-time', 'Part-time', 'Remote', 'On-site', 'Hybrid'];
  const workModes = ['On-site', 'Hybrid', 'Remote'];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const industryArray = Array.isArray(formData.industry) ? formData.industry : (formData.industry ? [formData.industry] : []);
    if (!formData.title || industryArray.length === 0 || !formData.about || !formData.contactEmail || !(formData.company || '').trim()) {
      alert('Please fill in all required fields: Internship Title, Industry / Domain, About, Company Name, and Contact Email');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/recruiters/internships'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          // Also include explicit privacy flags for clarity
          showCompanyName: formData.showCompanyName,
          showHrName: formData.showHrName,
          postedAt: new Date().toISOString(),
          status: 'active'
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Internship posted successfully!\n\nInternship ID: ${result._id || 'N/A'}\n\nYour internship is now live and will appear in your dashboard!`);
        navigate(backRoute);
      } else {
        const error = await response.json();
        alert(`Error: ${error.message || 'Failed to post internship'}`);
      }
    } catch (error) {
      console.error('Error posting internship:', error);
      alert('An error occurred while posting the internship');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="post-internship-container">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 post-internship-form">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Post an Internship</h1>
              <p className="mt-2 text-sm text-slate-600">
                Create an internship opportunity to discover talented students and fresh graduates
              </p>
            </div>
            <div className="flex w-full gap-3">
              <button
                type="button"
                onClick={() => navigate(backRoute)}
                className="post-internship-nav-btn flex-1 min-w-0 h-12 px-4 py-0 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style={{
                  background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box'
                }}
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={() => navigate(backRoute)}
                className="post-internship-nav-btn flex-1 min-w-0 h-12 px-4 py-0 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style={{
                  background: 'linear-gradient(135deg, #20B2AA 0%, #17a2b8 100%)',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section 1: Basic Internship Details */}
          <div className="post-internship-section">
            <h2>Basic Internship Details</h2>
            
            <div className="post-internship-grid">
              {/* Internship Title */}
              <div className="post-internship-field span-2">
                <label className="post-internship-label">
                  Internship Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Marketing Intern, AI Research Intern"
                  className="post-internship-input"
                  required
                />
              </div>

              {/* Company Name - recruiter only (recruiters post for different companies) */}
              {isRecruiter && (
                <div className="post-internship-field span-2">
                  <label className="post-internship-label">
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="e.g., Which company are you posting this internship for?"
                    className="post-internship-input"
                    required
                  />
                  <p className="post-internship-helper-text">Enter the company you are posting this internship for</p>
                </div>
              )}

              {/* Internship Type */}
              <LinkedInStyleSelect
                label="Internship Type"
                value={formData.internshipType}
                onChange={(val) => handleChange('internshipType', val)}
                options={internshipTypes}
                openUpwards={true}
                required
              />

              {/* Duration */}
              <LinkedInStyleSelect
                label="Duration"
                value={formData.duration}
                onChange={(val) => handleChange('duration', val)}
                options={internshipDurations}
                openUpwards={true}
                required
              />

              {/* Stipend Type */}
              <LinkedInStyleSelect
                label="Stipend Type"
                value={formData.stipendType}
                onChange={(val) => handleChange('stipendType', val)}
                options={stipendTypes}
                openUpwards={true}
                required
              />

              {/* Stipend Amount */}
              <div className="post-internship-field">
                <label className="post-internship-label">
                  Stipend Amount {formData.stipendType === 'Paid' && <span className="required">*</span>}
                </label>
                <div className="post-internship-currency-group">
                  <select
                    value={formData.stipendCurrency}
                    onChange={(e) => handleChange('stipendCurrency', e.target.value)}
                    className="post-internship-select"
                  >
                    {currencies.map(c => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={formData.stipendAmount}
                    onChange={(e) => handleChange('stipendAmount', e.target.value)}
                    placeholder="e.g., 500"
                    className="post-internship-input"
                    required={formData.stipendType === 'Paid'}
                  />
                </div>
                <p className="post-internship-helper-text">Per month or total (specify in description)</p>
              </div>

              {/* Eligibility */}
              <LinkedInStyleSelect
                label="Eligibility"
                value={formData.eligibility}
                onChange={(val) => handleChange('eligibility', val)}
                options={educationLevels}
                openUpwards={true}
                required
              />

              {/* Openings Available */}
              <div className="post-internship-field">
                <label className="post-internship-label">
                  Openings Available
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.openings}
                  onChange={(e) => handleChange('openings', parseInt(e.target.value) || 1)}
                  className="post-internship-input"
                />
              </div>

              {/* Skills Required */}
              <div className="post-internship-field span-2">
                <LinkedInStyleSelect
                  label="Skills Required"
                  value={formData.skills}
                  onChange={(val) => handleChange('skills', val)}
                  options={allSkills}
                  placeholder="Select required skills"
                  searchable
                  multiple
                  allowCustom
                  openUpwards={true}
                />
              </div>

              {/* Industry/Domain */}
              <LinkedInStyleSelect
                label="Industry / Domain"
                value={formData.industry}
                onChange={(val) => handleChange('industry', val)}
                options={industries}
                placeholder="Select industry"
                searchable
                multiple
                required
                openUpwards={true}
              />
            </div>
          </div>

          {/* Section 2: Internship Description */}
          <div className="post-internship-section">
            <h2>Internship Description</h2>
            
            <div className="space-y-6">
              {/* About the Internship */}
              <div className="post-internship-field">
                <label className="post-internship-label">
                  About the Internship <span className="required">*</span>
                </label>
                <textarea
                  value={formData.about}
                  onChange={(e) => handleChange('about', e.target.value)}
                  placeholder="Short summary + who would benefit from this internship"
                  rows="4"
                  className="post-internship-textarea"
                  required
                />
              </div>

              {/* Responsibilities */}
              <div className="post-internship-field">
                <label className="post-internship-label">
                  Responsibilities
                </label>
                <textarea
                  value={formData.responsibilities}
                  onChange={(e) => handleChange('responsibilities', e.target.value)}
                  placeholder="• Task 1&#10;• Task 2&#10;• Task 3&#10;(One responsibility per line)"
                  rows="5"
                  className="post-internship-textarea"
                />
              </div>

              {/* Learning Opportunities */}
              <div className="post-internship-field">
                <label className="post-internship-label">
                  Learning Opportunities
                </label>
                <textarea
                  value={formData.learningOpportunities}
                  onChange={(e) => handleChange('learningOpportunities', e.target.value)}
                  placeholder="What skills and knowledge will the intern gain from this experience?"
                  rows="4"
                  className="post-internship-textarea"
                />
              </div>

              {/* Additional Benefits */}
              <div className="post-internship-field">
                <label className="post-internship-label">
                  Additional Benefits
                </label>
                <div className="post-internship-checkbox-group">
                  <label className="post-internship-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.mentorship}
                      onChange={(e) => handleChange('mentorship', e.target.checked)}
                      className="post-internship-checkbox"
                    />
                    <span className="post-internship-checkbox-label">Mentorship Provided</span>
                  </label>
                  <label className="post-internship-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.certificate}
                      onChange={(e) => handleChange('certificate', e.target.checked)}
                      className="post-internship-checkbox"
                    />
                    <span className="post-internship-checkbox-label">Certificate of Completion Provided</span>
                  </label>
                  <label className="post-internship-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.jobOfferPossible}
                      onChange={(e) => handleChange('jobOfferPossible', e.target.checked)}
                      className="post-internship-checkbox"
                    />
                    <span className="post-internship-checkbox-label">Possible Job Offer After Internship</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Location & Duration */}
          <div className="post-internship-section">
            <h2>Location & Schedule</h2>
            
            <div className="post-internship-grid">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Work Mode */}
              <LinkedInStyleSelect
                label="Work Mode"
                value={formData.workMode}
                onChange={(val) => handleChange('workMode', val)}
                options={workModes}
                openUpwards={true}
                required
              />

              {/* Country */}
              <LinkedInStyleSelect
                label="Country"
                value={formData.country}
                onChange={(val) => handleChange('country', val)}
                options={countries}
                searchable
                required
              />

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., Nairobi"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Working Days per Week */}
              <LinkedInStyleSelect
                label="Working Days per Week"
                value={formData.workingDays}
                onChange={(val) => handleChange('workingDays', val)}
                options={workingDaysPerWeek}
              />

              {/* Working Hours */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Working Hours
                </label>
                <input
                  type="text"
                  value={formData.workingHours}
                  onChange={(e) => handleChange('workingHours', e.target.value)}
                  placeholder="e.g., 9 AM - 3 PM or Flexible"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Contact & Submission Details */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Contact & Company Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Application Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application Deadline
                </label>
                <input
                  type="date"
                  value={formData.applicationDeadline}
                  onChange={(e) => handleChange('applicationDeadline', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleChange('contactEmail', e.target.value)}
                  placeholder="hr@company.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* HR Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HR / Recruiter Name
                </label>
                <input
                  type="text"
                  value={formData.hrName}
                  onChange={(e) => handleChange('hrName', e.target.value)}
                  placeholder="e.g., Jane Smith"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Company Name - employers only (recruiters enter it in Basic section) */}
              {!isRecruiter && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="e.g., Maxpro Infotech Ltd"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {/* Company Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Website
                </label>
                <input
                  type="url"
                  value={formData.companyWebsite}
                  onChange={(e) => handleChange('companyWebsite', e.target.value)}
                  placeholder="https://www.company.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Company LinkedIn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company LinkedIn
                </label>
                <input
                  type="url"
                  value={formData.companyLinkedIn}
                  onChange={(e) => handleChange('companyLinkedIn', e.target.value)}
                  placeholder="https://www.linkedin.com/company/..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Visibility */}
              <LinkedInStyleSelect
                label="Internship Visibility"
                value={formData.visibility}
                onChange={(val) => handleChange('visibility', val)}
                options={visibilityOptions}
              />

              {/* Company Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Description
                </label>
                <textarea
                  value={formData.companyDescription}
                  onChange={(e) => handleChange('companyDescription', e.target.value)}
                  placeholder="Brief background about your company"
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Privacy: Show company name */}
              <div className="md:col-span-2 flex items-start gap-2">
                <input
                  id="intern-show-company-name"
                  type="checkbox"
                  checked={formData.showCompanyName}
                  onChange={(e) => handleChange('showCompanyName', e.target.checked)}
                  className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <label htmlFor="intern-show-company-name" className="block text-sm font-medium text-gray-700">
                    Show company name on public internship listing
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Keep this checked to display your company name to interns. Uncheck for a confidential posting.
                  </p>
                </div>
              </div>

              {/* Privacy: Show hiring manager name */}
              <div className="md:col-span-2 flex items-start gap-2">
                <input
                  id="intern-show-hr-name"
                  type="checkbox"
                  checked={formData.showHrName}
                  onChange={(e) => handleChange('showHrName', e.target.checked)}
                  className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <label htmlFor="intern-show-hr-name" className="block text-sm font-medium text-gray-700">
                    Show HR / recruiter name to candidates
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Keep this checked if you want applicants to see the hiring manager&apos;s name. Uncheck to keep this private.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="post-internship-submit-area">
            <div className="post-internship-submit-row">
              <p className="post-internship-required-text">
                * Required fields must be filled
              </p>
              <div className="flex w-full gap-3">
                <button
                  type="button"
                  onClick={() => navigate(backRoute)}
                  className="post-internship-nav-btn flex-1 min-w-0 h-12 px-4 py-0 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  style={{
                    background: 'linear-gradient(135deg, #20B2AA 0%, #17a2b8 100%)',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="post-internship-nav-btn flex-1 min-w-0 h-12 px-4 py-0 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box'
                  }}
                >
                  {loading ? 'Publishing...' : 'Publish Internship'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostInternship;

